import * as PIXI from 'pixi.js';
import { Vector2 } from './Vector2';

export class Camera {
  readonly container = new PIXI.Container();
  private _x = 0;
  private _y = 0;
  private _zoom = 1;
  private _screenWidth = 0;
  private _screenHeight = 0;
  private bounds: { width: number; height: number } | null = null;

  constructor(screenWidth: number, screenHeight: number) {
    this._screenWidth = screenWidth;
    this._screenHeight = screenHeight;
  }

  get x(): number { return this._x; }
  get y(): number { return this._y; }
  get zoom(): number { return this._zoom; }

  set screenWidth(value: number) { this._screenWidth = value; }
  set screenHeight(value: number) { this._screenHeight = value; }

  follow(target: Vector2): void {
    const targetX = target.x - this._screenWidth / (2 * this._zoom);
    const targetY = target.y - this._screenHeight / (2 * this._zoom);

    let newX = targetX;
    let newY = targetY;

    if (this.bounds) {
      newX = Math.max(0, Math.min(newX, this.bounds.width - this._screenWidth / this._zoom));
      newY = Math.max(0, Math.min(newY, this.bounds.height - this._screenHeight / this._zoom));
    }

    // Smooth follow
    this._x += (newX - this._x) * 0.1;
    this._y += (newY - this._y) * 0.1;

    this.container.x = -this._x * this._zoom;
    this.container.y = -this._y * this._zoom;
    this.container.scale.set(this._zoom);
  }

  setBounds(width: number, height: number): void {
    this.bounds = { width, height };
  }

  setZoom(zoom: number): void {
    this._zoom = zoom;
  }

  screenToWorld(screenX: number, screenY: number): Vector2 {
    return new Vector2(
      screenX / this._zoom + this._x,
      screenY / this._zoom + this._y,
    );
  }

  worldToScreen(worldX: number, worldY: number): Vector2 {
    return new Vector2(
      (worldX - this._x) * this._zoom,
      (worldY - this._y) * this._zoom,
    );
  }

  isVisible(x: number, y: number, width: number, height: number): boolean {
    const screenX = (x - this._x) * this._zoom;
    const screenY = (y - this._y) * this._zoom;
    const screenW = width * this._zoom;
    const screenH = height * this._zoom;

    return (
      screenX + screenW > 0 &&
      screenX < this._screenWidth &&
      screenY + screenH > 0 &&
      screenY < this._screenHeight
    );
  }
}
